import { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Music2,
  Baby,
  Flame,
  Sparkles,
  HandHeart,
  Heart,
  Shield,
  Video,
  ArrowLeft,
  ArrowUpRight,
  CheckCircle2,
  Mail,
  CalendarDays,
} from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { MINISTRIES, CHURCH } from "@/lib/data";

const ICONS = { Music2, Baby, Flame, Sparkles, HandHeart, Heart, Shield, Video };

export default function Ministry() {
  const { slug } = useParams();
  const ministry = useMemo(
    () => MINISTRIES.find((m) => m.slug === slug),
    [slug]
  );

  if (!ministry) return <Navigate to="/" replace />;

  const Icon = ICONS[ministry.icon] || Sparkles;
  const others = MINISTRIES.filter((m) => m.slug !== slug);
  const t = ministry.theme;

  return (
    <main
      style={t ? { backgroundColor: t.bg, color: t.text } : {}}
      className={t ? "" : "bg-[#050505] text-white"}
      data-testid={`ministry-page-${ministry.slug}`}
    >
      <Navbar />

      {/* HERO */}
      <section
        className="relative min-h-[80vh] flex items-end overflow-hidden"
        data-testid="ministry-hero"
      >
        {ministry.hero && (
          <div
            className="absolute inset-0 bg-cover bg-center scale-105"
            style={{ backgroundImage: `url(${ministry.hero})` }}
            aria-hidden="true"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55"
          style={t ? { background: `linear-gradient(to bottom, rgba(0,0,0,0.7), rgba(0,0,0,0.55), ${t.bg})` } : undefined}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pb-16 sm:pb-24 pt-32">
          <Link
            to="/#ministerios"
            data-testid="ministry-back-link"
            className="inline-flex items-center gap-2 text-xs tracking-overline uppercase text-white/60 hover:text-[#D4AF37] transition-colors mb-8"
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            Voltar para ministérios
          </Link>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37]">
              <Icon size={22} strokeWidth={1.5} />
            </div>
            <p className="text-xs tracking-overline uppercase text-[#D4AF37]">
              Ministério da PIBG
            </p>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            data-testid="ministry-title"
            className="font-serif-display text-5xl sm:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-tight max-w-4xl"
          >
            {ministry.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-6 max-w-2xl font-serif-display italic text-xl sm:text-2xl text-white/80"
            data-testid="ministry-tagline"
          >
            {ministry.tagline}
          </motion.p>
        </div>
      </section>

      {/* HISTÓRIA + ATIVIDADES */}
      <section className="py-24 sm:py-32" data-testid="ministry-content">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-7">
            <p className="text-xs tracking-overline uppercase mb-6" style={{ color: t ? t.accent : "#D4AF37" }}>
              Nossa História
            </p>
            <h2
              className="font-serif-display text-4xl sm:text-5xl leading-[1.05] tracking-tight mb-10"
              style={{ color: t ? t.text : "white" }}
            >
              Como tudo <span className="italic" style={{ color: t ? t.muted : "rgba(255,255,255,0.8)" }}>começou</span>.
            </h2>
            <div className="space-y-6 leading-relaxed text-lg" style={{ color: t ? t.muted : "rgba(255,255,255,0.7)" }}>
              {ministry.history.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div
              className="p-8 sm:p-10 sticky top-28"
              style={t
                ? { backgroundColor: t.accentLight, border: `1px solid ${t.accent}30` }
                : { backgroundColor: "#0A0A0A", border: "1px solid rgba(255,255,255,0.05)" }
              }
            >
              <p className="text-xs tracking-overline uppercase mb-6" style={{ color: t ? t.accent : "#D4AF37" }}>
                Atividades
              </p>
              <ul className="space-y-5" data-testid="ministry-activities">
                {ministry.activities.map((a, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <CheckCircle2
                      size={20}
                      strokeWidth={1.5}
                      className="mt-0.5 shrink-0"
                      style={{ color: t ? t.accent : "#D4AF37" }}
                    />
                    <span className="leading-relaxed" style={{ color: t ? t.text : "rgba(255,255,255,0.85)" }}>{a}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-10 pt-8" style={{ borderTop: `1px solid ${t ? t.accent + "30" : "rgba(255,255,255,0.1)"}` }}>
                <p className="text-sm leading-relaxed" style={{ color: t ? t.muted : "rgba(255,255,255,0.65)" }}>
                  {ministry.cta}
                </p>
                <Link
                  to="/#contato"
                  data-testid="ministry-contact-cta"
                  className="mt-6 inline-flex items-center gap-3 font-medium px-6 py-3 transition-colors"
                  style={t
                    ? { backgroundColor: t.accent, color: "white" }
                    : { backgroundColor: "#D4AF37", color: "black" }
                  }
                >
                  <Mail size={16} strokeWidth={1.8} />
                  Fale com a liderança
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* CITAÇÃO BÍBLICA — apenas Sisterhood */}
      {ministry.bibleQuote && (
        <section
          className="py-20 sm:py-24"
          style={{ backgroundColor: t?.accent + "18" }}
          data-testid="ministry-bible-quote"
        >
          <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
            <motion.blockquote
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="font-serif-display italic text-2xl sm:text-3xl leading-relaxed"
              style={{ color: t?.text }}
            >
              <span className="text-5xl leading-none select-none mr-1" style={{ color: t?.accent }}>"</span>
              {ministry.bibleQuote.text}
              <span className="text-5xl leading-none select-none ml-1" style={{ color: t?.accent }}>"</span>
            </motion.blockquote>
            <motion.cite
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-6 block text-sm tracking-overline uppercase not-italic"
              style={{ color: t?.accent }}
            >
              — {ministry.bibleQuote.ref}
            </motion.cite>
          </div>
        </section>
      )}

      {/* PRÓXIMOS ENCONTROS — apenas Sisterhood */}
      {ministry.upcomingEvents && (
        <section
          className="py-20 sm:py-28"
          style={{ backgroundColor: t?.bg }}
          data-testid="ministry-upcoming-events"
        >
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="max-w-2xl mb-14">
              <p className="text-xs tracking-overline uppercase mb-4" style={{ color: t?.accent }}>
                Agenda 2026
              </p>
              <h2 className="font-serif-display text-4xl sm:text-5xl leading-[1.05] tracking-tight" style={{ color: t?.text }}>
                Próximos <span className="italic" style={{ color: t?.accent }}>encontros</span>.
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {ministry.upcomingEvents.map((ev, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="p-6 flex flex-col gap-3"
                  style={{ backgroundColor: t?.accentLight, border: `1px solid ${t?.accent}30` }}
                >
                  <CalendarDays size={22} strokeWidth={1.5} style={{ color: t?.accent }} />
                  <p className="text-sm font-medium" style={{ color: t?.accent }}>{ev.date}</p>
                  <p className="text-base font-serif-display" style={{ color: t?.text }}>{ev.label}</p>
                </motion.div>
              ))}
            </div>
            <p className="mt-8 text-sm" style={{ color: t?.muted }}>
              Todo primeiro sábado do mês par · Horário divulgado nas redes sociais da PIBG.
            </p>
          </div>
        </section>
      )}

      {/* LIDERANÇA DO MINISTÉRIO */}
      <section
        className="py-24 sm:py-32"
        style={t
          ? { backgroundColor: t.accentLight, borderTop: `1px solid ${t.accent}20` }
          : { backgroundColor: "#0A0A0A", borderTop: "1px solid rgba(255,255,255,0.05)" }
        }
        data-testid="ministry-leaders"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-3xl mb-16">
            <p className="text-xs tracking-overline uppercase mb-6" style={{ color: t ? t.accent : "#D4AF37" }}>
              Quem está à frente
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" style={{ color: t ? t.text : "white" }}>
              Servos chamados <span className="italic" style={{ color: t ? t.muted : "rgba(255,255,255,0.8)" }}>para</span>{" "}
              <span style={{ color: t ? t.accent : "#D4AF37" }}>este ministério</span>.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {ministry.leaders.map((l, i) => (
              <motion.figure
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                data-testid={`ministry-leader-${i}`}
                className="group relative aspect-[4/5] overflow-hidden bg-[#050505]"
              >
                {l.image && (
                  <img
                    src={l.image}
                    alt={l.name}
                    className="absolute inset-0 w-full h-full object-cover object-top scale-105 group-hover:scale-110 transition-transform duration-700"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent" />
                <figcaption className="absolute bottom-0 left-0 right-0 p-7">
                  <p className="text-[10px] tracking-overline uppercase text-[#D4AF37] mb-2">
                    {l.role}
                  </p>
                  <h3 className="font-serif-display text-2xl text-white leading-tight">
                    {l.name}
                  </h3>
                  {l.bio && (
                    <p className="mt-3 text-sm text-white/70 leading-relaxed">
                      {l.bio}
                    </p>
                  )}
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* GALERIA DE FOTOS */}
      {ministry.gallery && ministry.gallery.length > 0 && (
        <section
          className="py-24 sm:py-32"
          style={t
            ? { backgroundColor: t.bg, borderTop: `1px solid ${t.accent}15` }
            : { borderTop: "1px solid rgba(255,255,255,0.05)" }
          }
          data-testid="ministry-gallery"
        >
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="max-w-3xl mb-16">
              <p className="text-xs tracking-overline uppercase mb-6" style={{ color: t ? t.accent : "#D4AF37" }}>
                Nossos Momentos
              </p>
              <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" style={{ color: t ? t.text : "white" }}>
                Fotos do <span style={{ color: t ? t.accent : "#D4AF37" }}>Ministério</span>.
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {ministry.gallery.map((photo, i) => (
                <motion.figure
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, delay: i * 0.1 }}
                  data-testid={`ministry-photo-${i}`}
                  className="group relative aspect-[4/3] overflow-hidden bg-[#050505]"
                >
                  <img
                    src={photo.url}
                    alt={photo.alt}
                    className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <figcaption className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <p className="text-sm text-white leading-relaxed">
                      {photo.caption}
                    </p>
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* OUTROS MINISTÉRIOS */}
      <section
        className="py-24 sm:py-32"
        style={t
          ? { backgroundColor: t.accentLight, borderTop: `1px solid ${t.accent}20` }
          : { borderTop: "1px solid rgba(255,255,255,0.05)" }
        }
        data-testid="ministry-others"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
            <div>
              <p className="text-xs tracking-overline uppercase mb-6" style={{ color: t ? t.accent : "#D4AF37" }}>
                Conheça também
              </p>
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight max-w-2xl" style={{ color: t ? t.text : "white" }}>
                Outros ministérios da{" "}
                <span style={{ color: t ? t.accent : "#D4AF37" }}>PIBG</span>.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px" style={{ backgroundColor: t ? t.accent + "20" : "rgba(255,255,255,0.05)" }}>
            {others.map((m) => {
              const ICN = ICONS[m.icon] || Sparkles;
              return (
                <Link
                  to={`/ministerios/${m.slug}`}
                  key={m.slug}
                  data-testid={`other-ministry-${m.slug}`}
                  className="group p-7 transition-all duration-500 flex flex-col justify-between min-h-[200px]"
                  style={{ backgroundColor: t ? t.bg : "#0A0A0A" }}
                >
                  <div className="flex items-start justify-between">
                    <div
                      className="w-10 h-10 flex items-center justify-center transition-all duration-500"
                      style={{ border: `1px solid ${t ? t.accent + "50" : "rgba(212,175,55,0.3)"}`, color: t ? t.accent : "#D4AF37" }}
                    >
                      <ICN size={18} strokeWidth={1.5} />
                    </div>
                    <ArrowUpRight
                      size={16}
                      strokeWidth={1.5}
                      style={{ color: t ? t.accent + "60" : "rgba(255,255,255,0.3)" }}
                    />
                  </div>
                  <h3 className="mt-8 font-serif-display text-2xl" style={{ color: t ? t.text : "white" }}>
                    {m.title}
                  </h3>
                </Link>
              );
            })}
          </div>

          <div className="mt-16 text-center">
            <Link
              to="/#contato"
              data-testid="ministry-final-cta"
              className="inline-flex items-center gap-3 px-8 py-4 transition-all duration-300"
              style={{ border: `1px solid ${t ? t.accent + "60" : "rgba(212,175,55,0.4)"}`, color: t ? t.accent : "#D4AF37" }}
            >
              Quero servir na {CHURCH.shortName}
              <ArrowUpRight size={16} strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
